import type { LogFn } from '../../log.js';
import { BRIDGE_APP_HEARTBEAT_INTERVAL_MS, BRIDGE_HEARTBEAT_MISSED_ACKS_BEFORE_RECONNECT } from '../heartbeat/constants.js';

/** Must match Bridge DO `closeSupersededBridgeSocket` close reason. */
export const BRIDGE_SUPERSEDED_CLOSE_REASON_SNIPPET = 'superseded';

/**
 * Sessions shorter than this before disconnect count as a "flap" (another bridge kicking us off).
 * Kept below the heartbeat reconnect window (~4 × 10s) so missed-heartbeat closes are excluded.
 */
export const DUPLICATE_BRIDGE_SHORT_SESSION_MS = 25_000;

/** Minimum short disconnects in the window before emitting the duplicate-bridge warning. */
export const DUPLICATE_BRIDGE_FLAP_MIN_COUNT = 2;

/** Rolling window for counting short disconnects. */
export const DUPLICATE_BRIDGE_FLAP_WINDOW_MS = 90_000;

/** After a session this long, clear flap history (stable connection). */
export const DUPLICATE_BRIDGE_STABLE_SESSION_MS =
  BRIDGE_APP_HEARTBEAT_INTERVAL_MS * BRIDGE_HEARTBEAT_MISSED_ACKS_BEFORE_RECONNECT + 5_000;

export const DUPLICATE_BRIDGE_WARNING_MESSAGE =
  '[Bridge service] It looks like two bridges are using the same token and disconnecting each other. ' +
  'Stop any duplicate @buildautomaton/cli process for this workspace/token (only one bridge should run).';

export type DuplicateBridgeFlapTracker = {
  sessionOpenedAtMs: number | null;
  shortDisconnectAtMs: number[];
};

export function createEmptyDuplicateBridgeFlapTracker(): DuplicateBridgeFlapTracker {
  return {
    sessionOpenedAtMs: null,
    shortDisconnectAtMs: [],
  };
}

export function isSupersededByNewBridgeClose(code: number, reason: string): boolean {
  if (code !== 1000) return false;
  return reason.toLowerCase().includes(BRIDGE_SUPERSEDED_CLOSE_REASON_SNIPPET);
}

function pruneShortDisconnects(tracker: DuplicateBridgeFlapTracker, nowMs: number): void {
  const cutoff = nowMs - DUPLICATE_BRIDGE_FLAP_WINDOW_MS;
  tracker.shortDisconnectAtMs = tracker.shortDisconnectAtMs.filter((t) => t >= cutoff);
}

export function recordBridgeSessionOpened(tracker: DuplicateBridgeFlapTracker, nowMs: number): void {
  tracker.sessionOpenedAtMs = nowMs;
}

export type DuplicateBridgeDetectResult =
  | { kind: 'none' }
  | { kind: 'warn'; trigger: 'superseded_close' | 'rapid_short_disconnects' };

/**
 * Call on main bridge WebSocket `close` while reconnecting. Returns whether to log
 * {@link DUPLICATE_BRIDGE_WARNING_MESSAGE} via {@link logDuplicateBridgeWarning}.
 */
export function evaluateDuplicateBridgeDisconnect(
  tracker: DuplicateBridgeFlapTracker,
  nowMs: number,
  code: number,
  reason: string,
): DuplicateBridgeDetectResult {
  if (isSupersededByNewBridgeClose(code, reason)) {
    return { kind: 'warn', trigger: 'superseded_close' };
  }

  const openedAt = tracker.sessionOpenedAtMs;
  tracker.sessionOpenedAtMs = null;
  if (openedAt == null) {
    return { kind: 'none' };
  }

  const sessionMs = nowMs - openedAt;
  if (sessionMs >= DUPLICATE_BRIDGE_STABLE_SESSION_MS) {
    tracker.shortDisconnectAtMs = [];
    return { kind: 'none' };
  }

  if (sessionMs < DUPLICATE_BRIDGE_SHORT_SESSION_MS) {
    pruneShortDisconnects(tracker, nowMs);
    tracker.shortDisconnectAtMs.push(nowMs);
    if (tracker.shortDisconnectAtMs.length >= DUPLICATE_BRIDGE_FLAP_MIN_COUNT) {
      return { kind: 'warn', trigger: 'rapid_short_disconnects' };
    }
  }

  return { kind: 'none' };
}

export function logDuplicateBridgeWarning(log: LogFn, result: DuplicateBridgeDetectResult): void {
  if (result.kind !== 'warn') return;
  try {
    log(DUPLICATE_BRIDGE_WARNING_MESSAGE);
  } catch {
    /* ignore */
  }
}
